import { Country, Type, WithdrawType } from "@core/logic/common/common";
import { Advertiser } from "@core/store/types/common/User";
import { AxiosPromise } from "axios";
import Service from "@core/services/common/Service";
import { VerticalsEnum } from "@core/store/types/admin/common/enums/VerticalsEnum";
import editCallCentersOfferQuery from "./graphql/editCallCentersOffer.graphql";
import { Money, User } from "@core/store/types/common";

type LandingParameters = "name" | "phone" | "surname" | "email" | "address" | "city" | "pcode" | "country" | "note2";

export interface Offer {
  name: string;
  offerType: string;
  withdrawType: string;
  minAllowedPayout: string;
  maxAllowedRate: string;
  rate: Money;
  isLaunched: boolean;
  landingCost: number;
  logo: Logo;
  previewLogo: Logo;
  maximalIndividualRate: Money;
  minimalIndividualRate: Money;
  usedSoftCapacity: number;
  totalSoftCapacity: number;
  duplicateTimeout: number;
  photoUrl: string;
  landingUrl: string;
  landingParameters: LandingParameters[];
  description: string;
  categories: string[];
  countries: string[];
  rules: string;
  totalTimeHold: number;
  preCost: number;
  cost: number;
  trafficTypes: string[];
  id: string;
  intId: number;
  createdAt: string;
  trackingPageId: Nullable<string>;
  offerMinimalCommission?: Nullable<Money>;
  minimalCommission?: Nullable<Money>;
  isExclusive: boolean;
  isApiAllowed: boolean;
  isResale: boolean;
  isTest: boolean;
  advertiserMinimalCommission?: Nullable<Money>;
  maximalIndividualRateadvertiserMinimalWriteOff?: Nullable<Money>;
  slug?: string;
  vertical: VerticalsEnum;
  advertiser: OfferAdvertiser;
  advertiserId: string;
}

interface Logo {
  id: string;
  url: string;
}

export interface OfferAdvertiser extends User {
  minimalCommission: Money;
}

interface OfferResponse {
  id: string;
  intId: number;
  name: string;
  type: Type;
  totalSoftCapacity: number;
  usedSoftCapacity: number;
  withdrawType: WithdrawType;
  rate: number;
  softCapacity: number;
  categories: null | Array<never>;
  countries: null | Country[];
  createdAt: Date;
  description: string | undefined;
  holdTime: number;
  isLaunched: boolean;
  landing: null | {
    cost: number;
    preCost: number;
    trackingPageId: string;
  };
  photoUrl: string;
  rules: string | undefined;
  landingUrl: string;
  logo: null | Logo;
  maximalIndividualRate: number | undefined;
  minimalIndividualRate: number | undefined;
  webmasters: Array<never>;
  advertisers: Array<never>;
}

export interface LandingResponse {
  id: string;
  name: string;
  offerId: string;
  discriminator: string;
  domains: Record<string, unknown>[];
  options: Record<string, unknown>;
  createdAt: Date;
}

export class Landing {
  public id: string;
  public name: string;
  public offerId: string;
  public discriminator: string;
  public domains: Record<string, unknown>[];
  public options: Record<string, unknown>;
  public createdAt: Date;

  constructor ({
    id,
    name,
    offerId,
    discriminator,
    domains,
    options,
    createdAt
  }: LandingResponse) {
    this.id = id;
    this.name = name;
    this.offerId = offerId;
    this.discriminator = discriminator;
    this.domains = domains;
    this.options = options;
    this.createdAt = createdAt;
  }
}

// tslint:disable-next-line:class-name
export class CreatedOffer {
  public id?: string;
  public intId?: number;
  public name?: string;
  public categories?: Array<string> | null = [];
  public description?: string;
  public countries?: Country[] | null = [];
  public type?: Type;
  public withdrawType?: WithdrawType;
  public rate?: number;
  public advertisers?: Array<Advertiser>;
  public minimalIndividualRate?: number;
  public maximalIndividualRate?: number;
  public holdTime?: number;
  public photoUrl?: string;
  public rules?: string;
  public landingUrl?: string;
  public logo?: Logo | null;
  public cost?: number;
  public preCost?: number;
  public isLaunched?: boolean;
  public createdAt?: Date;

  constructor (payload: OfferResponse | undefined) {
    if (payload) {
      this.id = payload.id;
      this.intId = payload.intId;
      this.name = payload.name;
      this.categories = payload.categories;
      this.description = payload.description;
      this.countries = payload.countries;
      this.type = payload.type;
      this.withdrawType = payload.withdrawType;
      this.rate = payload.rate;
      this.advertisers = payload.advertisers;
      this.minimalIndividualRate = payload.minimalIndividualRate;
      this.maximalIndividualRate = payload.maximalIndividualRate;
      this.holdTime = payload.holdTime;
      this.photoUrl = payload.photoUrl;
      this.rules = payload.rules;
      this.landingUrl = payload.landingUrl;
      this.preCost = payload.landing?.preCost;
      this.cost = payload.landing?.cost;
      this.logo = payload.logo;
      this.isLaunched = payload.isLaunched;
      this.createdAt = payload.createdAt;
    }
  }
}

// tslint:disable-next-line:max-classes-per-file
export class OfferService extends Service{
  public static getOffer (offerId: string): AxiosPromise<OfferResponse> {
    return this.rest.get(`/admin/offers/${ offerId }`);
  }

  public static updateOfferTrafficTypes (offerId: string, params: any): AxiosPromise<OfferResponse> {
    return this.rest.put(`/admin/offers/${ offerId }/trafficTypes`, { ...params });
  }

  public static getOfferWebmasters (offerId: string, params: any): AxiosPromise<OfferResponse> {
    return this.rest.get(`/admin/offers/${ offerId }/webmasters`, { params });
  }

  public static getOfferWebmastersAvailable (offerId: string, params: any): AxiosPromise<OfferResponse> {
    return this.rest.get(`/admin/offers/${ offerId }/webmasters/available`, { params });
  }

  public static getOfferAdvertisers (offerId: string, params: any): AxiosPromise<OfferResponse> {
    return this.rest.get(`/admin/offers/${ offerId }/advertisers`, { params });
  }

  public static getOfferAdvertiserWebmasters (offerId: string, params: any): AxiosPromise<OfferResponse> {
    return this.rest.get(`admin/rates/advertisers/offer/${ offerId }`, { params });
  }

  public static createOffer (offer: CreatedOffer): AxiosPromise<OfferResponse> {
    return this.rest.post("/admin/offers", offer);
  }

  public static async addOfferCallCenters (
    offerId: string,
    callCenterIds: any
  ): Promise<AxiosPromise<OfferResponse>> {
    try {
      const { data: { editCallCentersOffer } } = await this.api.post("", {
        query: editCallCentersOfferQuery,
        variables: {
          offerId,
          input: {
            callCenterIds
          }
        }
      });
      return editCallCentersOffer;
    } catch (e) {
      throw e;
    }
  }

  static getLandings (offerId: string, params: any): AxiosPromise<LandingResponse> {
    return this.rest.get(`admin/offers/${ offerId }/pages`, { params });
  }
}
